import { call, put, select, takeLatest } from 'redux-saga/effects';
import { TAction } from './types/action';
import history from '../../libs/history';
import { commonApiServiceAsync } from '../services/commonApiService';
import { createActionSetError, TErrorAction } from './errorActions';
import { t } from 'i18next';
import { commonRoutes } from '../../../gameConfig/commonRoutes';
import { resolveRoutePath } from '../../bundles/routes';

//
// Actions
//

type TGroupCharterClientState = {};

export type TActions = {
  showLoadingSpinner(show: boolean): TAction<boolean>;
  showError(info: any): TErrorAction;
  showInviteCode(show: boolean): TAction<boolean>;

  setListOpenGroupChartersRequested(): TAction<any>;
  setListOpenGroupChartersSucceeded(results: any): TAction<any>;
  setListOpenGroupChartersFailed(error: any): TAction<any>;

  setViewGroupCharterRequested(groupId: number): TAction<any>;
  setViewGroupCharterSucceeded(groupId: number, results: any): TAction<any>;
  setViewGroupCharterFailed(groupId: number, error: any): TAction<any>;

  setAddCharacterRequested(groupCharterId: number, characterId: number): TAction<any>;
  setAddCharacterSucceeded(groupCharterId: number, characterId: number, results: any): TAction<any>;
  setAddCharacterFailed(groupCharterId: number, characterId: number, error: any): TAction<any>;

  setRemoveCharacterRequested(groupCharterId: number, characterId: number): TAction<any>;
  setRemoveCharacterSucceeded(groupCharterId: number, characterId: number, results: any): TAction<any>;
  setRemoveCharacterFailed(groupCharterId: number, characterId: number, error: any): TAction<any>;

  setRemoveMemberRequested(groupCharterId: number, name: string): TAction<any>;
  setRemoveMemberSucceeded(groupCharterId: number, name: string, results: any): TAction<any>;
  setRemoveMemberFailed(groupCharterId: number, name: string, error: any): TAction<any>;

  setTransferOwnerRequested(groupCharterId: number, userId: number): TAction<any>;
  setTransferOwnerSucceeded(groupCharterId: number, userId: number, results: any): TAction<any>;
  setTransferOwnerFailed(groupCharterId: number, userId: number, error: any): TAction<any>;

  createGroupCharterRequested(params: any): TAction<any>;
  createGroupCharterSucceeded(results: any): TAction<any>;
  createGroupCharterFailed(error: any): TAction<any>;

  renameGroupCharterRequested(params: any): TAction<any>;
  renameGroupCharterSucceeded(results: any): TAction<any>;
  renameGroupCharterFailed(error: any): TAction<any>;

  leaveGroupCharterRequested(groupCharterId: number): TAction<any>;
  leaveGroupCharterSucceeded(groupCharterId: number): TAction<any>;
  leaveGroupCharterFailed(groupCharterId: number, error: any): TAction<any>;

  deleteGroupCharterRequested(groupCharterId: number): TAction<any>;
  deleteGroupCharterSucceeded(groupCharterId: number): TAction<any>;
  deleteGroupCharterFailed(groupCharterId: number, error: any): TAction<any>;

  setViewGroupCharterInviteRequested(code: string): TAction<any>;
  setViewGroupCharterInviteSucceeded(code: string, results: any): TAction<any>;
  setViewGroupCharterInviteFailed(code: string, error: any): TAction<any>;

  setAcceptGroupCharterInviteRequested(code: string): TAction<any>;
  setAcceptGroupCharterInviteSucceeded(results: any): TAction<any>;
  setAcceptGroupCharterInviteFailed(error: any): TAction<any>;

  setRegenerateGroupCharterInviteCodeRequested(groupCharterId: number): TAction<any>;
  setRegenerateGroupCharterInviteCodeSucceeded(results: any): TAction<any>;
  setRegenerateGroupCharterInviteCodeFailed(groupCharterId: number, error: any): TAction<any>;

  setDeleteGroupCharterInviteCodeRequested(groupCharterId: number): TAction<any>;
  setDeleteGroupCharterInviteCodeSucceeded(results: any): TAction<any>;
  setDeleteGroupCharterInviteCodeFailed(groupCharterId: number, error: any): TAction<any>;

  createTeamFromGroupCharterRequested(params: any): TAction<any>;
  createTeamFromGroupCharterSucceeded(results: any): TAction<any>;
  createTeamFromGroupCharterFailed(error: any): TAction<any>;
};

export const actionTypes = {
  SHOW_LOADING_SPINNER: 'GROUP_CHARTER/SHOW_LOADING_SPINNER',
  SHOW_INVITE_CODE: 'GROUP_CHARTER/SHOW_INVITE_CODE',

  SET_LIST_OPEN_GROUP_CHARTERS_REQUESTED: 'GROUP_CHARTER/LIST_OPEN_GROUP_CHARTERS_REQUESTED',
  SET_LIST_OPEN_GROUP_CHARTERS_SUCCEEDED: 'GROUP_CHARTER/LIST_OPEN_GROUP_CHARTERS_SUCCEEDED',
  SET_LIST_OPEN_GROUP_CHARTERS_FAILED: 'GROUP_CHARTER/LIST_OPEN_GROUP_CHARTERS_FAILED',

  SET_VIEW_GROUP_CHARTER_REQUESTED: 'GROUP_CHARTER/VIEW_REQUESTED',
  SET_VIEW_GROUP_CHARTER_SUCCEEDED: 'GROUP_CHARTER/VIEW_SUCCEEDED',
  SET_VIEW_GROUP_CHARTER_FAILED: 'GROUP_CHARTER/VIEW_FAILED',

  SET_ADD_CHARACTER_REQUESTED: 'GROUP_CHARTER/ADD_CHARACTER_REQUESTED',
  SET_ADD_CHARACTER_SUCCEEDED: 'GROUP_CHARTER/ADD_CHARACTER_SUCCEEDED',
  SET_ADD_CHARACTER_FAILED: 'GROUP_CHARTER/ADD_CHARACTER_FAILED',

  SET_REMOVE_CHARACTER_REQUESTED: 'GROUP_CHARTER/REMOVE_CHARACTER_REQUESTED',
  SET_REMOVE_CHARACTER_SUCCEEDED: 'GROUP_CHARTER/REMOVE_CHARACTER_SUCCEEDED',
  SET_REMOVE_CHARACTER_FAILED: 'GROUP_CHARTER/REMOVE_CHARACTER_FAILED',

  SET_REMOVE_MEMBER_REQUESTED: 'GROUP_CHARTER/REMOVE_MEMBER_REQUESTED',
  SET_REMOVE_MEMBER_SUCCEEDED: 'GROUP_CHARTER/REMOVE_MEMBER_SUCCEEDED',
  SET_REMOVE_MEMBER_FAILED: 'GROUP_CHARTER/REMOVE_MEMBER_FAILED',

  SET_TRANSFER_OWNER_REQUESTED: 'GROUP_CHARTER/SET_TRANSFER_OWNER_REQUESTED',
  SET_TRANSFER_OWNER_SUCCEEDED: 'GROUP_CHARTER/SET_TRANSFER_OWNER_SUCCEEDED',
  SET_TRANSFER_OWNER_FAILED: 'GROUP_CHARTER/SET_TRANSFER_OWNER_FAILED',

  DELETE_GROUP_CHARTER_REQUESTED: 'GROUP_CHARTER/DELETE_GROUP_CHARTER_REQUESTED',
  DELETE_GROUP_CHARTER_SUCCEEDED: 'GROUP_CHARTER/DELETE_GROUP_CHARTER_SUCCEEDED',
  DELETE_GROUP_CHARTER_FAILED: 'GROUP_CHARTER/DELETE_GROUP_CHARTER_FAILED',

  LEAVE_GROUP_CHARTER_REQUESTED: 'GROUP_CHARTER/LEAVE_GROUP_CHARTER_REQUESTED',
  LEAVE_GROUP_CHARTER_SUCCEEDED: 'GROUP_CHARTER/LEAVE_GROUP_CHARTER_SUCCEEDED',
  LEAVE_GROUP_CHARTER_FAILED: 'GROUP_CHARTER/LEAVE_GROUP_CHARTER_FAILED',

  CREATE_GROUP_CHARTER_REQUESTED: 'GROUP_CHARTER/CREATE_GROUP_CHARTER_REQUESTED',
  CREATE_GROUP_CHARTER_SUCCEEDED: 'GROUP_CHARTER/CREATE_GROUP_CHARTER_SUCCEEDED',
  CREATE_GROUP_CHARTER_FAILED: 'GROUP_CHARTER/CREATE_GROUP_CHARTER_FAILED',

  RENAME_GROUP_CHARTER_REQUESTED: 'GROUP_CHARTER/RENAME_GROUP_CHARTER_REQUESTED',
  RENAME_GROUP_CHARTER_SUCCEEDED: 'GROUP_CHARTER/RENAME_GROUP_CHARTER_SUCCEEDED',
  RENAME_GROUP_CHARTER_FAILED: 'GROUP_CHARTER/RENAME_GROUP_CHARTER_FAILED',

  VIEW_INVITE_REQUESTED: 'GROUP_CHARTER/VIEW_INVITE_REQUESTED',
  VIEW_INVITE_SUCCEEDED: 'GROUP_CHARTER/VIEW_INVITE_SUCCEEDED',
  VIEW_INVITE_FAILED: 'GROUP_CHARTER/VIEW_INVITE_FAILED',

  ACCEPT_INVITE_REQUESTED: 'GROUP_CHARTER/ACCEPT_INVITE_REQUESTED',
  ACCEPT_INVITE_SUCCEEDED: 'GROUP_CHARTER/ACCEPT_INVITE_SUCCEEDED',
  ACCEPT_INVITE_FAILED: 'GROUP_CHARTER/ACCEPT_INVITE_FAILED',

  SET_REGENERATE_INVITE_CODE_REQUESTED: 'GROUP_CHARTER/SET_REGENERATE_INVITE_CODE_REQUESTED',
  SET_REGENERATE_INVITE_CODE_SUCCEEDED: 'GROUP_CHARTER/SET_REGENERATE_INVITE_CODE_SUCCEEDED',
  SET_REGENERATE_INVITE_CODE_FAILED: 'GROUP_CHARTER/SET_REGENERATE_INVITE_CODE_FAILED',

  SET_DELETE_INVITE_CODE_REQUESTED: 'GROUP_CHARTER/SET_DELETE_INVITE_CODE_REQUESTED',
  SET_DELETE_INVITE_CODE_SUCCEEDED: 'GROUP_CHARTER/SET_DELETE_INVITE_CODE_SUCCEEDED',
  SET_DELETE_INVITE_CODE_FAILED: 'GROUP_CHARTER/SET_DELETE_INVITE_CODE_FAILED',

  CREATE_TEAM_FROM_GROUP_CHARTER_REQUESTED: 'GROUP_CHARTER/CREATE_TEAM_FROM_GROUP_CHARTER_REQUESTED',
  CREATE_TEAM_FROM_GROUP_CHARTER_SUCCEEDED: 'GROUP_CHARTER/CREATE_TEAM_FROM_GROUP_CHARTER_SUCCEEDED',
  CREATE_TEAM_FROM_GROUP_CHARTER_FAILED: 'GROUP_CHARTER/CREATE_TEAM_FROM_GROUP_CHARTER_FAILED'
};

export const actions: TActions = {
  showLoadingSpinner(show: boolean) {
    return { type: actionTypes.SHOW_LOADING_SPINNER, payload: show };
  },

  showInviteCode(show: boolean) {
    return { type: actionTypes.SHOW_INVITE_CODE, payload: show };
  },

  showError: createActionSetError,

  //
  // List Open Group Charters (Nav)
  //
  setListOpenGroupChartersRequested() {
    return { type: actionTypes.SET_LIST_OPEN_GROUP_CHARTERS_REQUESTED, payload: {} };
  },

  setListOpenGroupChartersSucceeded(results: any) {
    return { type: actionTypes.SET_LIST_OPEN_GROUP_CHARTERS_SUCCEEDED, payload: { results } };
  },

  setListOpenGroupChartersFailed(error: any) {
    return { type: actionTypes.SET_LIST_OPEN_GROUP_CHARTERS_FAILED, payload: error };
  },

  //
  // View Group
  //
  setViewGroupCharterRequested(groupId: number) {
    return { type: actionTypes.SET_VIEW_GROUP_CHARTER_REQUESTED, payload: { groupId } };
  },

  setViewGroupCharterSucceeded(groupId: number, results: any) {
    return { type: actionTypes.SET_VIEW_GROUP_CHARTER_SUCCEEDED, payload: { groupId, results } };
  },

  setViewGroupCharterFailed(groupId: number, error: any) {
    return { type: actionTypes.SET_VIEW_GROUP_CHARTER_FAILED, payload: { groupId, error } };
  },

  //
  // Add Character
  //
  setAddCharacterRequested(groupCharterId: number, characterId: number) {
    return { type: actionTypes.SET_ADD_CHARACTER_REQUESTED, payload: { groupCharterId, characterId } };
  },

  setAddCharacterSucceeded(groupCharterId: number, characterId: number, results: any) {
    return { type: actionTypes.SET_ADD_CHARACTER_SUCCEEDED, payload: { groupCharterId, characterId, results } };
  },

  setAddCharacterFailed(groupCharterId: number, characterId: number, error: any) {
    return { type: actionTypes.SET_ADD_CHARACTER_FAILED, payload: error };
  },

  //
  // Remove Character
  //
  setRemoveCharacterRequested(groupCharterId: number, characterId: number) {
    return { type: actionTypes.SET_REMOVE_CHARACTER_REQUESTED, payload: { groupCharterId, characterId } };
  },

  setRemoveCharacterSucceeded(groupCharterId: number, characterId: number, results: any) {
    return { type: actionTypes.SET_REMOVE_CHARACTER_SUCCEEDED, payload: { groupCharterId, characterId, results } };
  },

  setRemoveCharacterFailed(groupCharterId: number, characterId: number, error: any) {
    return { type: actionTypes.SET_REMOVE_CHARACTER_FAILED, payload: error };
  },

  //
  // Remove Member
  //
  setRemoveMemberRequested(groupCharterId: number, name: string) {
    return { type: actionTypes.SET_REMOVE_MEMBER_REQUESTED, payload: { groupCharterId, name } };
  },

  setRemoveMemberSucceeded(groupCharterId: number, name: string, results: any) {
    return { type: actionTypes.SET_REMOVE_MEMBER_SUCCEEDED, payload: { groupCharterId, name, results } };
  },

  setRemoveMemberFailed(groupCharterId: number, name: string, error: any) {
    return { type: actionTypes.SET_REMOVE_MEMBER_FAILED, payload: error };
  },

  setTransferOwnerRequested(groupCharterId: number, userId: number) {
    return { type: actionTypes.SET_TRANSFER_OWNER_REQUESTED, payload: { groupCharterId, userId } };
  },

  setTransferOwnerSucceeded(groupCharterId: number, userId: number, results: any) {
    return { type: actionTypes.SET_TRANSFER_OWNER_SUCCEEDED, payload: { groupCharterId, userId, results } };
  },

  setTransferOwnerFailed(groupCharterId: number, userId: number, error: any) {
    return { type: actionTypes.SET_TRANSFER_OWNER_FAILED, payload: error };
  },

  //
  // Delete group charter
  //
  deleteGroupCharterRequested(groupCharterId: number) {
    return { type: actionTypes.DELETE_GROUP_CHARTER_REQUESTED, payload: { groupCharterId } };
  },

  deleteGroupCharterSucceeded(groupCharterId: number) {
    return { type: actionTypes.DELETE_GROUP_CHARTER_SUCCEEDED, payload: { groupCharterId } };
  },

  deleteGroupCharterFailed(groupCharterId: number, error: any) {
    return { type: actionTypes.DELETE_GROUP_CHARTER_FAILED, payload: error };
  },

  //
  // Leave group charter
  //
  leaveGroupCharterRequested(groupCharterId: number) {
    return { type: actionTypes.LEAVE_GROUP_CHARTER_REQUESTED, payload: { groupCharterId } };
  },

  leaveGroupCharterSucceeded(groupCharterId: number) {
    return { type: actionTypes.LEAVE_GROUP_CHARTER_SUCCEEDED, payload: { groupCharterId } };
  },

  leaveGroupCharterFailed(groupCharterId: number, error: any) {
    return { type: actionTypes.LEAVE_GROUP_CHARTER_FAILED, payload: error };
  },

  //
  // Create group charter
  //
  createGroupCharterRequested(params: any) {
    return { type: actionTypes.CREATE_GROUP_CHARTER_REQUESTED, payload: params };
  },

  createGroupCharterSucceeded(results: any) {
    return { type: actionTypes.CREATE_GROUP_CHARTER_SUCCEEDED, payload: { results } };
  },

  createGroupCharterFailed(error: any) {
    return { type: actionTypes.CREATE_GROUP_CHARTER_FAILED, payload: error };
  },

  //
  // Rename group charter
  //
  renameGroupCharterRequested(params: any) {
    return { type: actionTypes.RENAME_GROUP_CHARTER_REQUESTED, payload: params };
  },

  renameGroupCharterSucceeded(results: any) {
    return { type: actionTypes.RENAME_GROUP_CHARTER_SUCCEEDED, payload: { results } };
  },

  renameGroupCharterFailed(error: any) {
    return { type: actionTypes.RENAME_GROUP_CHARTER_FAILED, payload: error };
  },

  //
  // View Group Invite
  //
  setViewGroupCharterInviteRequested(code: string) {
    return { type: actionTypes.VIEW_INVITE_REQUESTED, payload: { code } };
  },
  setViewGroupCharterInviteSucceeded(code: string, results: any) {
    return { type: actionTypes.VIEW_INVITE_SUCCEEDED, payload: { code, ...results } };
  },
  setViewGroupCharterInviteFailed(code: string, error: any) {
    return { type: actionTypes.VIEW_INVITE_FAILED, payload: error };
  },

  //
  // Accept Group Invite
  //
  setAcceptGroupCharterInviteRequested(code: string) {
    return { type: actionTypes.ACCEPT_INVITE_REQUESTED, payload: { code } };
  },
  setAcceptGroupCharterInviteSucceeded(results: any) {
    return { type: actionTypes.ACCEPT_INVITE_SUCCEEDED, payload: results };
  },
  setAcceptGroupCharterInviteFailed(error: any) {
    return { type: actionTypes.ACCEPT_INVITE_FAILED, payload: error };
  },

  //
  // Regenerate Group Charter Invite Code
  //
  setRegenerateGroupCharterInviteCodeRequested(groupCharterId: number) {
    return { type: actionTypes.SET_REGENERATE_INVITE_CODE_REQUESTED, payload: { groupCharterId } };
  },

  setRegenerateGroupCharterInviteCodeSucceeded(results: any) {
    return { type: actionTypes.SET_REGENERATE_INVITE_CODE_SUCCEEDED, payload: { results } };
  },

  setRegenerateGroupCharterInviteCodeFailed(groupCharterId: number, error: any) {
    return { type: actionTypes.SET_REGENERATE_INVITE_CODE_FAILED, payload: error };
  },

  //
  // Delete Group Charter Invite Code
  //
  setDeleteGroupCharterInviteCodeRequested(groupCharterId: number) {
    return { type: actionTypes.SET_DELETE_INVITE_CODE_REQUESTED, payload: { groupCharterId } };
  },

  setDeleteGroupCharterInviteCodeSucceeded(results: any) {
    return { type: actionTypes.SET_DELETE_INVITE_CODE_SUCCEEDED, payload: { results } };
  },

  setDeleteGroupCharterInviteCodeFailed(groupCharterId: number, error: any) {
    return { type: actionTypes.SET_DELETE_INVITE_CODE_FAILED, payload: error };
  },

  //
  // Create team from charter
  //
  createTeamFromGroupCharterRequested(params: any) {
    return { type: actionTypes.CREATE_TEAM_FROM_GROUP_CHARTER_REQUESTED, payload: params };
  },

  createTeamFromGroupCharterSucceeded(results: any) {
    return { type: actionTypes.CREATE_TEAM_FROM_GROUP_CHARTER_SUCCEEDED, payload: { results } };
  },

  createTeamFromGroupCharterFailed(error: any) {
    return { type: actionTypes.CREATE_TEAM_FROM_GROUP_CHARTER_FAILED, payload: error };
  }
};

//
// Reducer
//

function getInitialState() {
  return {
    isShowingInviteCode: false,

    createTeamFromGroupCharter: {
      isRequested: false,
      isSucceeded: false,
      isErrored: false
    },

    regenerateInvite: {
      isRequested: false,
      isSucceeded: false,
      isErrored: false
    },

    deleteInvite: {
      isRequested: false,
      isSucceeded: false,
      isErrored: false
    },

    acceptInvite: {
      isRequested: false,
      isSucceeded: false,
      isErrored: false
    },

    viewInvite: {
      isRequested: false,
      isSucceeded: false,
      isErrored: false
    },

    viewGroupCharter: {
      isRequested: false,
      isSucceeded: false,
      isErrored: false
    },

    openGroupCharters: {
      isRequested: false,
      isSucceeded: false,
      isErrored: false
    },

    createGroupCharter: {
      isRequested: false,
      isSucceeded: false,
      isErrored: false
    },

    renameGroupCharter: {
      isRequested: false,
      isSucceeded: false,
      isErrored: false
    },

    deleteGroupCharter: {
      isRequested: false,
      isSucceeded: false,
      isErrored: false
    },

    removeCharacter: {
      isRequested: false,
      isSucceeded: false,
      isErrored: false
    },

    addCharacter: {
      isRequested: false,
      isSucceeded: false,
      isErrored: false
    }
  };
}

export function reducer(state: any = getInitialState(), action: TAction<any>): Partial<TGroupCharterClientState> {
  switch (action.type) {
    case actionTypes.SHOW_LOADING_SPINNER: {
      return {
        ...state,
        isLoading: action.payload
      };
    }

    case actionTypes.SHOW_INVITE_CODE: {
      return {
        ...state,
        isShowingInviteCode: action.payload
      };
    }

    case actionTypes.SET_LIST_OPEN_GROUP_CHARTERS_REQUESTED: {
      return {
        ...state,
        openGroupCharters: {
          ...state.openGroupCharters,
          isRequested: true,
          isSucceeded: false,
          isErrored: false
        }
      };
    }

    case actionTypes.SET_LIST_OPEN_GROUP_CHARTERS_SUCCEEDED: {
      return {
        ...state,
        openGroupCharters: {
          ...state.openGroupCharters,
          isRequested: false,
          isSucceeded: true,
          isErrored: false,
          ...action.payload.results
        }
      };
    }

    case actionTypes.SET_LIST_OPEN_GROUP_CHARTERS_FAILED: {
      return {
        ...state,
        openGroupCharters: {
          ...state.openGroupCharters,
          isRequested: false,
          isSucceeded: false,
          isErrored: true,
          error: action.payload
        }
      };
    }

    case actionTypes.SET_VIEW_GROUP_CHARTER_REQUESTED: {
      return {
        ...state,
        viewGroupCharter: {
          groupId: action.payload.groupId,
          isRequested: true,
          isSucceeded: false,
          isErrored: false
        }
      };
    }

    case actionTypes.SET_VIEW_GROUP_CHARTER_SUCCEEDED: {
      return {
        ...state,
        viewGroupCharter: {
          isRequested: false,
          isSucceeded: true,
          isErrored: false,
          groupId: action.payload.groupId,
          ...action.payload.results
        }
      };
    }

    case actionTypes.SET_VIEW_GROUP_CHARTER_FAILED: {
      return {
        ...state,
        viewGroupCharter: {
          ...state.viewGroupCharter,
          isRequested: false,
          isSucceeded: false,
          isErrored: true,
          error: action.payload
        }
      };
    }

    case actionTypes.SET_REGENERATE_INVITE_CODE_REQUESTED: {
      return {
        ...state,
        regenerateInvite: {
          isRequested: true,
          isSucceeded: false,
          isErrored: false
        }
      };
    }
    case actionTypes.SET_REGENERATE_INVITE_CODE_SUCCEEDED: {
      return {
        ...state,
        viewGroupCharter: {
          ...state.viewGroupCharter,
          ...action.payload.results
        },
        isShowingInviteCode: true,
        regenerateInvite: {
          isRequested: false,
          isSucceeded: true,
          isErrored: false
        }
      };
    }
    case actionTypes.SET_REGENERATE_INVITE_CODE_FAILED: {
      return {
        ...state,
        regenerateInvite: {
          isRequested: false,
          isSucceeded: false,
          isErrored: true
        }
      };
    }

    case actionTypes.SET_DELETE_INVITE_CODE_REQUESTED: {
      return {
        ...state,
        deleteInvite: {
          isRequested: true,
          isSucceeded: false,
          isErrored: false
        }
      };
    }
    case actionTypes.SET_DELETE_INVITE_CODE_SUCCEEDED: {
      return {
        ...state,
        viewGroupCharter: {
          ...state.viewGroupCharter,
          ...action.payload.results
        },
        isShowingInviteCode: false,
        deleteInvite: {
          isRequested: false,
          isSucceeded: true,
          isErrored: false
        }
      };
    }
    case actionTypes.SET_DELETE_INVITE_CODE_FAILED: {
      return {
        ...state,
        deleteInvite: {
          isRequested: false,
          isSucceeded: false,
          isErrored: true
        }
      };
    }

    case actionTypes.CREATE_TEAM_FROM_GROUP_CHARTER_REQUESTED: {
      return {
        ...state,
        createTeamFromGroupCharter: {
          isRequested: true,
          isSucceeded: false,
          isErrored: false
        }
      };
    }
    case actionTypes.CREATE_TEAM_FROM_GROUP_CHARTER_SUCCEEDED: {
      return {
        ...state,
        createTeamFromGroupCharter: {
          isRequested: false,
          isSucceeded: true,
          isErrored: false
        }
      };
    }
    case actionTypes.CREATE_TEAM_FROM_GROUP_CHARTER_FAILED: {
      return {
        ...state,
        createTeamFromGroupCharter: {
          isRequested: false,
          isSucceeded: false,
          isErrored: true
        }
      };
    }

    case actionTypes.DELETE_GROUP_CHARTER_REQUESTED: {
      return {
        ...state,
        deleteGroupCharter: {
          groupCharterId: action.payload.groupCharterId,
          isRequested: true,
          isSucceeded: false,
          isErrored: false
        }
      };
    }

    case actionTypes.DELETE_GROUP_CHARTER_SUCCEEDED: {
      return {
        ...state,
        deleteGroupCharter: {
          groupCharterId: action.payload.groupCharterId,
          isRequested: false,
          isSucceeded: true,
          isErrored: false,
          ...action.payload.results
        }
      };
    }

    case actionTypes.DELETE_GROUP_CHARTER_FAILED: {
      return {
        ...state,
        deleteGroupCharter: {
          groupCharterId: action.payload.groupCharterId,
          isRequested: false,
          isSucceeded: false,
          isErrored: true,
          error: action.payload
        }
      };
    }

    case actionTypes.LEAVE_GROUP_CHARTER_REQUESTED: {
      return {
        ...state,
        leaveGroupCharter: {
          groupCharterId: action.payload.groupCharterId,
          isRequested: true,
          isSucceeded: false,
          isErrored: false
        }
      };
    }

    case actionTypes.LEAVE_GROUP_CHARTER_SUCCEEDED: {
      return {
        ...state,
        leaveGroupCharter: {
          groupCharterId: action.payload.groupCharterId,
          isRequested: false,
          isSucceeded: true,
          isErrored: false,
          ...action.payload.results
        }
      };
    }

    case actionTypes.LEAVE_GROUP_CHARTER_FAILED: {
      return {
        ...state,
        leaveGroupCharter: {
          groupCharterId: action.payload.groupCharterId,
          isRequested: false,
          isSucceeded: false,
          isErrored: true,
          error: action.payload
        }
      };
    }

    case actionTypes.CREATE_GROUP_CHARTER_REQUESTED: {
      return {
        ...state,
        createGroupCharter: {
          isRequested: true,
          isSucceeded: false,
          isErrored: false
        }
      };
    }

    case actionTypes.CREATE_GROUP_CHARTER_SUCCEEDED: {
      return {
        ...state,
        createGroupCharter: {
          isRequested: false,
          isSucceeded: true,
          isErrored: false
        }
      };
    }

    case actionTypes.CREATE_GROUP_CHARTER_FAILED: {
      return {
        ...state,
        createGroupCharter: {
          isRequested: false,
          isSucceeded: false,
          isErrored: true,
          error: action.payload
        }
      };
    }

    case actionTypes.RENAME_GROUP_CHARTER_REQUESTED: {
      return {
        ...state,
        renameGroupCharter: {
          isRequested: true,
          isSucceeded: false,
          isErrored: false
        }
      };
    }

    case actionTypes.RENAME_GROUP_CHARTER_SUCCEEDED: {
      return {
        ...state,
        renameGroupCharter: {
          isRequested: false,
          isSucceeded: true,
          isErrored: false
        },

        // clear the group charter so it gets loaded again
        viewGroupCharter: {
          isRequested: false,
          isSucceeded: false,
          isErrored: false
        }
      };
    }

    case actionTypes.RENAME_GROUP_CHARTER_FAILED: {
      return {
        ...state,
        renameGroupCharter: {
          isRequested: false,
          isSucceeded: false,
          isErrored: true,
          error: action.payload
        }
      };
    }

    case actionTypes.SET_ADD_CHARACTER_REQUESTED: {
      return {
        ...state,
        addCharacter: {
          ...state.addCharacter,
          isRequested: true,
          isSucceeded: false,
          isErrored: false
        }
      };
    }

    case actionTypes.SET_ADD_CHARACTER_SUCCEEDED: {
      return {
        ...state,
        viewGroupCharter: {
          ...state.viewGroupCharter,
          ...action.payload.results
        },
        addCharacter: {
          ...state.addCharacter,
          isRequested: false,
          isSucceeded: true,
          isErrored: false
        }
      };
    }

    case actionTypes.SET_ADD_CHARACTER_FAILED: {
      return {
        ...state,
        addCharacter: {
          ...state.addCharacter,
          isRequested: false,
          isSucceeded: false,
          isErrored: true,
          error: action.payload
        }
      };
    }

    case actionTypes.SET_REMOVE_CHARACTER_REQUESTED: {
      return {
        ...state,
        removeCharacter: {
          request: action.payload,
          isRequested: true,
          isSucceeded: false,
          isErrored: false
        }
      };
    }

    case actionTypes.SET_REMOVE_CHARACTER_SUCCEEDED: {
      return {
        ...state,
        viewGroupCharter: {
          ...state.viewGroupCharter,
          ...action.payload.results
        },
        removeCharacter: {
          ...state.removeCharacter,
          isRequested: false,
          isSucceeded: true,
          isErrored: false
        }
      };
    }

    case actionTypes.SET_REMOVE_CHARACTER_FAILED: {
      return {
        ...state,
        removeCharacter: {
          ...state.removeCharacter,
          isRequested: false,
          isSucceeded: false,
          isErrored: true,
          error: action.payload
        }
      };
    }

    case actionTypes.SET_REMOVE_MEMBER_REQUESTED: {
      return {
        ...state,
        removeMember: {
          request: action.payload,
          isRequested: true,
          isSucceeded: false,
          isErrored: false
        }
      };
    }

    case actionTypes.SET_REMOVE_MEMBER_SUCCEEDED: {
      return {
        ...state,
        viewGroupCharter: {
          ...state.viewGroupCharter,
          ...action.payload.results
        },
        removeMember: {
          ...state.removeMember,
          isRequested: false,
          isSucceeded: true,
          isErrored: false
        }
      };
    }

    case actionTypes.SET_REMOVE_MEMBER_FAILED: {
      return {
        ...state,
        removeMember: {
          ...state.removeMember,
          isRequested: false,
          isSucceeded: false,
          isErrored: true,
          error: action.payload
        }
      };
    }

    case actionTypes.SET_TRANSFER_OWNER_REQUESTED: {
      return {
        ...state,
        transferOwner: {
          request: action.payload,
          isRequested: true,
          isSucceeded: false,
          isErrored: false
        }
      };
    }

    case actionTypes.SET_TRANSFER_OWNER_SUCCEEDED: {
      return {
        ...state,
        viewGroupCharter: {
          ...state.viewGroupCharter,
          ...action.payload.results
        },
        transferOwner: {
          ...state.removeMember,
          isRequested: false,
          isSucceeded: true,
          isErrored: false
        }
      };
    }

    case actionTypes.SET_TRANSFER_OWNER_FAILED: {
      return {
        ...state,
        transferOwner: {
          ...state.removeMember,
          isRequested: false,
          isSucceeded: false,
          isErrored: true,
          error: action.payload
        }
      };
    }

    case actionTypes.VIEW_INVITE_REQUESTED: {
      return {
        ...state,
        viewInvite: {
          isRequested: true,
          isSucceeded: false,
          isErrored: false
        }
      };
    }
    case actionTypes.VIEW_INVITE_SUCCEEDED: {
      return {
        ...state,
        viewInvite: {
          isRequested: false,
          isSucceeded: true,
          isErrored: false,
          ...action.payload
        }
      };
    }
    case actionTypes.VIEW_INVITE_FAILED: {
      return {
        ...state,
        viewInvite: {
          isRequested: false,
          isSucceeded: false,
          isErrored: true,
          error: action.payload
        }
      };
    }

    case actionTypes.ACCEPT_INVITE_REQUESTED: {
      return {
        ...state,
        acceptInvite: {
          isRequested: true,
          isSucceeded: false,
          isErrored: false
        }
      };
    }
    case actionTypes.ACCEPT_INVITE_SUCCEEDED: {
      return {
        ...getInitialState(), // reset anything that was cached when accepting new invite
        acceptInvite: {
          isRequested: false,
          isSucceeded: true,
          isErrored: false,
          ...action.payload
        }
      };
    }
    case actionTypes.ACCEPT_INVITE_FAILED: {
      return {
        ...state,
        acceptInvite: {
          isRequested: false,
          isSucceeded: false,
          isErrored: true,
          error: action.payload
        }
      };
    }
  }

  return state;
}

//
// Sagas
//

//
// list open group charters
//
function* listOpenGroupCharters(action: TAction<any>): any {
  try {
    const results = yield call(commonApiServiceAsync.listOpenGroupCharters);
    yield put(actions.setListOpenGroupChartersSucceeded(results));
  } catch (e) {
    yield put(actions.setListOpenGroupChartersFailed(e.toString()));
  }
}

function* watchListOpenGroupChartersRequested(): any {
  // TODO: should this be a `takeEvery` so if someone hits multiple create buttons it will create each one?
  yield takeLatest(actionTypes.SET_LIST_OPEN_GROUP_CHARTERS_REQUESTED, listOpenGroupCharters);
}

//
// view group charter by group id
//
function* viewGroupCharter(action: TAction<any>): any {
  const { groupId } = action.payload;
  try {
    const results = yield call(commonApiServiceAsync.getGroupCharter, { id: groupId });
    const auth = yield select((state: any) => state.auth);
    if (results.groupCharter?.rename_requested_at && auth?.id === results.owner?.id) {
      // force them to the rename page
      history.push(resolveRoutePath(commonRoutes.RenameGroupCharterPage, { group_id: results.groupCharter.group_id }));
    } else {
      yield put(actions.setViewGroupCharterSucceeded(groupId, results));
    }
  } catch (e) {
    yield put(actions.setViewGroupCharterFailed(groupId, e.toString()));
    yield put(
      actions.showError({
        title: 'Unable to View Group Charter',
        body: e.toString()
      })
    );
  }
}

function* watchViewGroupCharterRequested(): any {
  // TODO: should this be a `takeEvery` so if someone hits multiple create buttons it will create each one?
  yield takeLatest(actionTypes.SET_VIEW_GROUP_CHARTER_REQUESTED, viewGroupCharter);
}

//
// add character
//
function* addCharacter(action: TAction<any>): any {
  const { groupCharterId, characterId } = action.payload;
  try {
    const results = yield call(commonApiServiceAsync.addCharacterToGroupCharter, {
      groups_charter_id: groupCharterId,
      character_id: characterId
    });
    yield put(
      actions.setAddCharacterSucceeded(groupCharterId, characterId, {
        groupCharterId,
        characterId,
        ...results
      })
    );
  } catch (e) {
    yield put(actions.setAddCharacterFailed(groupCharterId, characterId, e.toString()));
    yield put(
      actions.showError({
        title: 'Failed to Add Character to Group',
        body: e.toString()
      })
    );
  }
}

function* watchAddCharacterRequested(): any {
  // TODO: should this be a `takeEvery` so if someone hits multiple create buttons it will create each one?
  yield takeLatest(actionTypes.SET_ADD_CHARACTER_REQUESTED, addCharacter);
}

//
// remove character
//
function* removeCharacterFromGroupCharter(action: TAction<any>): any {
  const { groupCharterId, characterId } = action.payload;
  try {
    const results = yield call(commonApiServiceAsync.removeCharacterFromGroupCharter, {
      groups_charter_id: groupCharterId,
      character_id: characterId
    });
    yield put(
      actions.setRemoveCharacterSucceeded(groupCharterId, characterId, {
        groupCharterId,
        characterId,
        ...results
      })
    );
  } catch (e) {
    yield put(actions.setRemoveCharacterFailed(groupCharterId, characterId, e.toString()));
    yield put(
      actions.showError({
        title: 'Failed to Remove Charter from Group',
        body: e.toString()
      })
    );
  }
}

function* watchRemoveCharacterRequested(): any {
  // TODO: should this be a `takeEvery` so if someone hits multiple create buttons it will create each one?
  yield takeLatest(actionTypes.SET_REMOVE_CHARACTER_REQUESTED, removeCharacterFromGroupCharter);
}

//
// remove member
//
function* removeMemberFromGroupCharter(action: TAction<any>): any {
  const { groupCharterId, name } = action.payload;
  const groupCharter = yield select((state) => state.groupCharter.viewGroupCharter);
  try {
    const results = yield call(commonApiServiceAsync.removeMemberFromGroupCharter, {
      groups_charter_id: groupCharterId,
      name
    });
    if (results.success) {
      yield put(
        actions.setRemoveMemberSucceeded(groupCharterId, name, {
          groupCharterId,
          name,
          ...groupCharter,
          userMembersInCharter: groupCharter.userMembersInCharter.filter((member: any) => {
            return member.name !== name;
          })
        })
      );
    }
  } catch (e) {
    yield put(actions.setRemoveMemberFailed(groupCharterId, name, e.toString()));
    yield put(
      actions.showError({
        title: 'Failed to Remove Charter from Group',
        body: e.toString()
      })
    );
  }
}

function* watchRemoveMemberRequested(): any {
  yield takeLatest(actionTypes.SET_REMOVE_MEMBER_REQUESTED, removeMemberFromGroupCharter);
}

function* transferOwnerFromGroupCharter(action: TAction<any>): any {
  const { groupCharterId, userId } = action.payload;
  const groupCharter = yield select((state) => state.groupCharter.viewGroupCharter);
  try {
    const results = yield call(commonApiServiceAsync.transferGroupCharterOwner, {
      groups_charter_id: groupCharterId,
      userId
    });
    if (results.success) {
      yield put(
        actions.setTransferOwnerSucceeded(groupCharterId, userId, {
          groupCharterId,
          userId,
          ...groupCharter,
          owner: groupCharter.userMembersInCharter.find((member: any) => {
            return member.id === userId;
          })
        })
      );
    }
  } catch (e) {
    yield put(actions.setTransferOwnerFailed(groupCharterId, userId, e.toString()));
    yield put(
      actions.showError({
        title: 'Failed to Transfer Owner',
        body: e.toString()
      })
    );
  }
}

function* watchTransferOwnerRequested(): any {
  yield takeLatest(actionTypes.SET_TRANSFER_OWNER_REQUESTED, transferOwnerFromGroupCharter);
}

//
// delete group charter
//
function* deleteGroupCharter(action: TAction<any>): any {
  const { groupCharterId } = action.payload;
  try {
    yield call(commonApiServiceAsync.deleteGroupCharter, {
      groups_charter_id: groupCharterId
    });
    yield put(actions.deleteGroupCharterSucceeded(groupCharterId));
    yield put(actions.setListOpenGroupChartersRequested());
    history.push(commonRoutes.UserBattleNetSettings);
  } catch (e) {
    yield put(actions.deleteGroupCharterFailed(groupCharterId, e.toString()));
    yield put(
      actions.showError({
        title: 'Failed to Delete Charter',
        body: e.toString()
      })
    );
  }
}

function* watchLeaveGroupCharterRequested(): any {
  // TODO: should this be a `takeEvery` so if someone hits multiple create buttons it will create each one?
  yield takeLatest(actionTypes.LEAVE_GROUP_CHARTER_REQUESTED, leaveGroupCharter);
}

//
// leave group charter
//
function* leaveGroupCharter(action: TAction<any>): any {
  const { groupCharterId } = action.payload;
  try {
    yield call(commonApiServiceAsync.leaveGroupCharter, {
      groups_charter_id: groupCharterId
    });
    yield put(actions.leaveGroupCharterSucceeded(groupCharterId));
    yield put(actions.setListOpenGroupChartersRequested());
    history.push(commonRoutes.UserBattleNetSettings);
  } catch (e) {
    yield put(actions.leaveGroupCharterFailed(groupCharterId, e.toString()));
    yield put(
      actions.showError({
        title: 'Failed to Leave Charter',
        body: e.toString()
      })
    );
  }
}

function* watchDeleteGroupCharterRequested(): any {
  // TODO: should this be a `takeEvery` so if someone hits multiple create buttons it will create each one?
  yield takeLatest(actionTypes.DELETE_GROUP_CHARTER_REQUESTED, deleteGroupCharter);
}

//
// create group charter
//
function* createGroupCharter(action: TAction<any>): any {
  try {
    const results = yield call(commonApiServiceAsync.createGroupCharter, action.payload);
    yield put(actions.createGroupCharterSucceeded(results));
    yield put(actions.setListOpenGroupChartersRequested());
    history.push(resolveRoutePath(commonRoutes.ViewGroupCharterSettings, { group_id: results.group_id }));
  } catch (e) {
    yield put(actions.createGroupCharterFailed(e.toString()));
    yield put(
      actions.showError({
        title: 'Failed to Create Charter',
        body: e.toString()
      })
    );
  }
}

function* watchCreateGroupCharterRequested(): any {
  // TODO: should this be a `takeEvery` so if someone hits multiple create buttons it will create each one?
  yield takeLatest(actionTypes.CREATE_GROUP_CHARTER_REQUESTED, createGroupCharter);
}

//
// rename group charter
//
function* renameGroupCharter(action: TAction<any>): any {
  try {
    const results = yield call(commonApiServiceAsync.renameGroupCharter, action.payload);
    yield put(actions.renameGroupCharterSucceeded(results));
    yield put(actions.setListOpenGroupChartersRequested());
    const currentFeatures = yield select((state: any) => state.currentFeatures);
    if (currentFeatures?.admin || currentFeatures?.support_team) {
      history.push(resolveRoutePath(commonRoutes.UserGroupSettings, { group_id: results.group_id }));
    } else {
      history.push(resolveRoutePath(commonRoutes.ViewGroupCharterSettings, { group_id: results.group_id }));
    }
  } catch (e) {
    yield put(actions.renameGroupCharterFailed(e.toString()));
    yield put(
      actions.showError({
        title: 'Failed to Rename Charter',
        body: e.toString()
      })
    );
  }
}

function* watchRenameGroupCharterRequested(): any {
  // TODO: should this be a `takeEvery` so if someone hits multiple rename buttons it will rename each one?
  yield takeLatest(actionTypes.RENAME_GROUP_CHARTER_REQUESTED, renameGroupCharter);
}

//
// view group charter invite
//
function* viewGroupCharterInvite(action: TAction<any>): any {
  const { code } = action.payload;
  try {
    const results = yield call(commonApiServiceAsync.viewGroupCharterInvite, code);
    if (results.loginRequired) {
      history.push(
        `${commonRoutes.Login}?next=${encodeURIComponent(resolveRoutePath(commonRoutes.GroupCharterInvite, { code }))}`
      );
    } else {
      yield put(actions.setViewGroupCharterInviteSucceeded(code, results));
    }
  } catch (e) {
    yield put(actions.setViewGroupCharterInviteFailed(code, e.toString()));
  }
}

function* watchViewGroupCharterInviteRequested(): any {
  // TODO: should this be a `takeEvery` so if someone hits multiple create buttons it will create each one?
  yield takeLatest(actionTypes.VIEW_INVITE_REQUESTED, viewGroupCharterInvite);
}

//
// accept group charter invite
//
function* acceptGroupCharterInvite(action: TAction<any>): any {
  const { code } = action.payload;
  try {
    const results = yield call(commonApiServiceAsync.acceptGroupCharterInvite, code);
    yield put(actions.setAcceptGroupCharterInviteSucceeded(results));
    yield put(actions.setListOpenGroupChartersRequested());
  } catch (e) {
    yield put(createActionSetError({ title: t('common:errors.something-went-wrong'), body: e.toString() }));
  }
}

function* watchAcceptGroupCharterInviteRequested(): any {
  // TODO: should this be a `takeEvery` so if someone hits multiple create buttons it will create each one?
  yield takeLatest(actionTypes.ACCEPT_INVITE_REQUESTED, acceptGroupCharterInvite);
}

//
// regenerate group charter invite
//
function* regenerateGroupCharterInvite(action: TAction<any>): any {
  const { groupCharterId } = action.payload;

  try {
    const results = yield call(commonApiServiceAsync.regenerateGroupCharterInvite, { charterId: groupCharterId });
    yield put(actions.setRegenerateGroupCharterInviteCodeSucceeded(results));
  } catch (e) {
    yield put(actions.setRegenerateGroupCharterInviteCodeFailed(groupCharterId, e.toString()));
  }
}

function* watchRegenerateGroupCharterInviteRequested(): any {
  // TODO: should this be a `takeEvery` so if someone hits multiple create buttons it will create each one?
  yield takeLatest(actionTypes.SET_REGENERATE_INVITE_CODE_REQUESTED, regenerateGroupCharterInvite);
}

//
// delete invite
//
function* deleteGroupCharterInviteCode(action: TAction<any>): any {
  const { groupCharterId } = action.payload;
  try {
    const results = yield call(commonApiServiceAsync.deleteGroupCharterInvite, groupCharterId);
    yield put(actions.setDeleteGroupCharterInviteCodeSucceeded(results));
  } catch (e) {
    yield put(actions.setDeleteGroupCharterInviteCodeFailed(groupCharterId, e.toString()));
  }
}

function* watchDeleteGroupCharterInviteCodeRequested(): any {
  // TODO: should this be a `takeEvery` so if someone hits multiple create buttons it will create each one?
  yield takeLatest(actionTypes.SET_DELETE_INVITE_CODE_REQUESTED, deleteGroupCharterInviteCode);
}

//
// create team from charter
//
function* createTeamFromGroupCharter(action: TAction<any>): any {
  try {
    const results = yield call(commonApiServiceAsync.createTeamFromGroupCharter, { id: action.payload });
    yield put(actions.createTeamFromGroupCharterSucceeded(results));
    yield put(actions.setListOpenGroupChartersRequested());
    history.push(resolveRoutePath(commonRoutes.UserGroupSettings, { group_id: results.group_id }));
  } catch (e) {
    yield put(actions.createTeamFromGroupCharterFailed(e.toString()));
    yield put(
      actions.showError({
        title: 'Failed to Create Mythic+ Team',
        body: e.toString()
      })
    );
  }
}

function* watchCreateTeamFromGroupCharterRequested(): any {
  // TODO: should this be a `takeEvery` so if someone hits multiple create buttons it will create each one?
  yield takeLatest(actionTypes.CREATE_TEAM_FROM_GROUP_CHARTER_REQUESTED, createTeamFromGroupCharter);
}

//
// saga exports
//
export const sagas = [
  watchViewGroupCharterRequested(),
  watchAddCharacterRequested(),
  watchRemoveCharacterRequested(),
  watchRemoveMemberRequested(),
  watchTransferOwnerRequested(),
  watchListOpenGroupChartersRequested(),
  watchDeleteGroupCharterRequested(),
  watchLeaveGroupCharterRequested(),
  watchCreateGroupCharterRequested(),
  watchRenameGroupCharterRequested(),
  watchViewGroupCharterInviteRequested(),
  watchAcceptGroupCharterInviteRequested(),
  watchRegenerateGroupCharterInviteRequested(),
  watchCreateTeamFromGroupCharterRequested(),
  watchDeleteGroupCharterInviteCodeRequested()
];
